import {
  type Definition,
  datamuseQuery,
  isName,
  splitEcho,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";
import { arpabetToIpa } from "@/lib/tool-kit/vocabulary/ipa";

/**
 * The Homophone Finder.
 *
 * The sets come from `public/data/cmu-homophones.txt`, built by
 * `scripts/build-homophones.mjs` from the CMU Pronouncing Dictionary (BSD-style
 * licence, reproduced in the file's header): one line per exact pronunciation that is
 * shared, once stress is ignored, by two or more words. Every pronunciation variant is
 * kept, so "read" is a homophone of "red" (R EH1 D) and of "reed" (R IY1 D). Matching
 * is on whole phoneme strings — the same sounds in the same order — never on spelling.
 * The dictionary records General American English, which merges some pairs that
 * speakers elsewhere keep apart (cot and caught, or Mary, merry and marry), and keeps
 * apart some that other accents merge (such as the r-dropping pairs of non-rhotic
 * accents).
 *
 * Datamuse adds what the dictionary cannot: how common each word is, and a definition.
 * Its `rel_hom` ("homophones of") answers with the same words and, with `md=dpf`,
 * their definitions, parts of speech and frequencies in a single request — so a search
 * costs one lookup however many homophones the word has. The page still works when
 * Datamuse does not answer: the sets then show without definitions, ordered by the
 * dictionary's own marker for names and unusual spellings.
 */

/** Where `scripts/build-homophones.mjs` writes the sets. */
export const HOMOPHONES_URL = "/data/cmu-homophones.txt";

/** Words used fewer times per million than this go under "less common". */
export const COMMON_FREQUENCY = 0.5;

const MAX_RESULTS = 100;
const MAX_DEFINITIONS = 2;
const RE_LINE_BREAK = /\r?\n/;
const RE_STRESS = /[012]$/;
const RE_CURLY_APOSTROPHE = /[’‘]/g;
const RE_SPACE = /\s+/g;

/* --------------------------------------------------------------------- data */

export interface SetWord {
  word: string;
  /** Not in the ENABLE word list: a name, a letter, or an unusual spelling. */
  unusual: boolean;
}

export interface RawGroup {
  /** ARPAbet of the searched word that this group matches, e.g. `R EH1 D`. */
  pron: string;
  words: SetWord[];
}

export interface HomophoneIndex {
  /** Number of pronunciation lines in the file. */
  size: number;
  /** The homophone sets for a word, one per pronunciation. Empty when it has none. */
  groupsFor: (word: string, ignoreStress: boolean) => RawGroup[];
}

export function stripStress(pron: string): string {
  return pron
    .split(" ")
    .map((phone) => phone.replace(RE_STRESS, ""))
    .join(" ");
}

function parseWord(raw: string): SetWord {
  return raw.startsWith("~")
    ? { word: raw.slice(1), unusual: true }
    : { word: raw, unusual: false };
}

/** A typed word as the file keys it: lower case, straight apostrophes, single spaces. */
export function normalizeHomophoneQuery(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(RE_CURLY_APOSTROPHE, "'")
    .replace(RE_SPACE, " ");
}

/** The file's text → an index. Comment and blank lines are dropped. */
export function parseHomophoneIndex(text: string): HomophoneIndex {
  const exact = new Map<string, SetWord[]>();
  const wordProns = new Map<string, string[]>();
  const relaxed = new Map<string, Map<string, SetWord>>();
  let size = 0;
  for (const line of text.split(RE_LINE_BREAK)) {
    if (!line || line.startsWith("#")) {
      continue;
    }
    const tab = line.indexOf("\t");
    if (tab === -1) {
      continue;
    }
    size += 1;
    const pron = line.slice(0, tab);
    const key = stripStress(pron);
    const words = line
      .slice(tab + 1)
      .split(" ")
      .map(parseWord);
    exact.set(pron, words);
    const merged = relaxed.get(key) ?? new Map<string, SetWord>();
    for (const entry of words) {
      merged.set(entry.word, entry);
      const prons = wordProns.get(entry.word) ?? [];
      prons.push(pron);
      wordProns.set(entry.word, prons);
    }
    relaxed.set(key, merged);
  }

  function groupsFor(word: string, ignoreStress: boolean): RawGroup[] {
    const prons = wordProns.get(word) ?? [];
    const groups: RawGroup[] = [];
    const seen = new Set<string>();
    for (const pron of prons) {
      const key = ignoreStress ? stripStress(pron) : pron;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      const source = ignoreStress
        ? [...(relaxed.get(key)?.values() ?? [])]
        : (exact.get(pron) ?? []);
      const words = source.filter((entry) => entry.word !== word);
      if (words.length > 0) {
        groups.push({ pron, words });
      }
    }
    return groups;
  }

  return { size, groupsFor };
}

let index: Promise<HomophoneIndex> | null = null;

/** The sets, fetched once per page load. A failed fetch is forgotten, so retry works. */
export function loadHomophoneIndex(): Promise<HomophoneIndex> {
  if (!index) {
    index = fetch(HOMOPHONES_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Homophone data returned ${response.status}`);
        }
        return response.text();
      })
      .then(parseHomophoneIndex)
      .catch((error: unknown) => {
        index = null;
        throw error;
      });
  }
  return index;
}

/* ------------------------------------------------------------------ Datamuse */

/** One request: the homophones of a word, with definitions and frequencies. */
export function homophoneQuery(word: string): string {
  return datamuseQuery({
    rel_hom: word,
    md: "dpf",
    qe: "rel_hom",
    max: MAX_RESULTS,
  });
}

/** The searched word, from an answered query string. */
export function wordOfHomophoneQuery(query: string): string {
  return new URLSearchParams(query).get("rel_hom") ?? "";
}

/** The first definition of each part of speech, at most two in all. */
export function shortDefinitions(
  definitions: readonly Definition[]
): Definition[] {
  const out: Definition[] = [];
  for (const definition of definitions) {
    if (out.length >= MAX_DEFINITIONS) {
      break;
    }
    if (!out.some((kept) => kept.pos === definition.pos)) {
      out.push(definition);
    }
  }
  return out;
}

export interface HomophoneEntry {
  word: string;
  /** Occurrences per million words, when Datamuse knows the word. */
  frequency: number | null;
  definitions: Definition[];
  /** A name or an unusual spelling, listed apart from the everyday words. */
  uncommon: boolean;
}

export interface HomophoneGroup {
  pron: string;
  /** Broad General American IPA with stress marks, or null if it cannot be written. */
  ipa: string | null;
  common: HomophoneEntry[];
  uncommon: HomophoneEntry[];
}

function entryFor(
  item: SetWord,
  known: ReadonlyMap<string, WordResult> | null
): HomophoneEntry {
  const result = known?.get(item.word);
  if (!result) {
    return {
      word: item.word,
      frequency: null,
      definitions: [],
      uncommon: item.unusual,
    };
  }
  const frequency = result.frequency;
  const rare = frequency === null ? item.unusual : frequency < COMMON_FREQUENCY;
  return {
    word: item.word,
    frequency,
    definitions: shortDefinitions(result.definitions),
    uncommon: rare || isName(result),
  };
}

function byFrequency(a: HomophoneEntry, b: HomophoneEntry): number {
  return (
    (b.frequency ?? -1) - (a.frequency ?? -1) || a.word.localeCompare(b.word)
  );
}

/**
 * Combine the dictionary's sets with Datamuse's answer (null when it did not answer):
 * each group splits into everyday words and names or unusual spellings, most frequent
 * first, with definitions where Datamuse supplied them.
 */
export function buildHomophoneGroups(
  raw: readonly RawGroup[],
  answer: readonly WordResult[] | null
): HomophoneGroup[] {
  const known = answer
    ? new Map(splitEcho(answer).results.map((result) => [result.word, result]))
    : null;
  return raw.map((group) => {
    const entries = group.words
      .map((item) => entryFor(item, known))
      .sort(byFrequency);
    return {
      pron: group.pron,
      ipa: arpabetToIpa(group.pron),
      common: entries.filter((entry) => !entry.uncommon),
      uncommon: entries.filter((entry) => entry.uncommon),
    };
  });
}

/** `pos: definition` for a result row. */
export function definitionLine(
  entry: HomophoneEntry,
  posNames: Readonly<Record<string, string>>
): string {
  return entry.definitions
    .map(
      (definition) =>
        `${posNames[definition.pos] ?? definition.pos}: ${definition.text}`
    )
    .join(" · ");
}
